import { Button, DropdownMenu, Icon, Select, SvgIconNames } from '@design';
import { Option } from '@terminal/components/filter/Option';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { WebSearch } from '@terminal/views/alfred/research/services/conversations-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import clsx from 'clsx';
import React, { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrustedSourcesModal } from './TrustedSourcesModal';
import styles from './WebSearchDropdown.module.scss';
import { useCustomSources } from '@terminal/react-query-hooks/alfred/useCustomSources';

interface DropdownOption {
  key: string;
  name: string;
  icon: SvgIconNames;
  description: string;
  value: {
    web_search: WebSearch;
  };
  isChecked: boolean;
}

export type WebSearchDropdownVariant = 'icon' | 'select';

export interface WebSearchDropdownProps {
  contentRef?: RefObject<HTMLDivElement | null>;
  variant?: WebSearchDropdownVariant;
}

export const WebSearchDropdown = ({
  contentRef,
  variant = 'icon',
}: WebSearchDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { webSearch, setWebSearch } = useChat(state => ({
    webSearch: state.webSearch,
    setWebSearch: state.setWebSearch,
  }));
  const { trackAlfredWebSearchSelected } = usePosthogEvents();

  const { data: customSourcesData } = useCustomSources();
  const emptyCustomSources = customSourcesData?.data?.sources?.length === 0;

  useEffect(() => {
    if (emptyCustomSources && webSearch === 'restricted') {
      setWebSearch('block');
    }
  }, [customSourcesData, webSearch]);

  const options: DropdownOption[] = [
    {
      key: 'open',
      name: t('chat:trustedSources.general.title'),
      icon: 'Globe',
      description: t('chat:trustedSources.general.description'),
      value: {
        web_search: 'open',
      },
      isChecked: webSearch === 'open',
    },
    {
      key: 'restricted',
      name: t('chat:trustedSources.restricted.title'),
      icon: 'GlobeLocked',
      description: t('chat:trustedSources.restricted.description'),
      value: {
        web_search: 'restricted',
      },
      isChecked: webSearch === 'restricted',
    },
    {
      key: 'block',
      name: t('chat:trustedSources.block.title'),
      icon: 'GlobeOff',
      description: t('chat:trustedSources.block.description'),
      value: {
        web_search: 'block',
      },
      isChecked: webSearch === 'block',
    },
  ];

  const selectedOption = options.find(
    option => option.value.web_search === webSearch
  );

  const handleModalOpen = () => {
    setDropdownOpen(false);
    setModalOpen(true);
  };

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <div className={styles.optionWrapper}>
        {filterOption.icon && <Icon iconName={filterOption.icon} size={16} />}
        <span className={styles.label}>{filterOption.name}</span>
      </div>
    );
  };

  const renderTrigger = () => {
    if (variant === 'select') {
      return (
        <div className={styles.selectWrapper}>
          <Select
            isActive={dropdownOpen}
            inputProps={{
              value: selectedOption?.name ?? '',
              variant: 'outline',
              'data-testid': 'web-search-dropdown-trigger',
              leftIcon: selectedOption?.icon ? (
                <Icon iconName={selectedOption.icon} size={16} />
              ) : undefined,
            }}
          />
        </div>
      );
    }

    return (
      <div>
        <Button
          className={styles.stylesButton}
          variant="empty"
          iconName={selectedOption?.icon}
          size="small"
          data-testid="web-search-dropdown-trigger"
        />
      </div>
    );
  };

  const dropdownMenu = (
    <DropdownMenu
      contentRef={contentRef}
      triggerNode={renderTrigger()}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      className={clsx(styles.styledDropdownMenu, {
        [styles.select]: variant === 'select',
      })}
      contentProps={
        variant === 'select'
          ? {
              style: {
                width: 'var(--radix-dropdown-menu-trigger-width)',
                minWidth: 'var(--radix-dropdown-menu-trigger-width)',
                maxWidth: 'var(--radix-dropdown-menu-trigger-width)',
              },
            }
          : undefined
      }
    >
      {options.map(option => {
        return (
          <div key={option.key}>
            <Option
              option={option}
              isChecked={option.isChecked}
              indentedDescription={24}
              handleClick={() => {
                if (
                  option.value.web_search === 'restricted' &&
                  emptyCustomSources
                ) {
                  handleModalOpen();
                }

                setWebSearch(option.value.web_search);
                const typeMap: Record<
                  WebSearch,
                  'general' | 'trusted' | 'none'
                > = {
                  open: 'general',
                  restricted: 'trusted',
                  block: 'none',
                };
                trackAlfredWebSearchSelected(
                  POSTHOG_TRACKING_SOURCES.ALFRED,
                  typeMap[option.value.web_search]
                );
              }}
              renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
            />
            {option.key === 'restricted' && (
              <Button
                className={styles.stylesXsButton}
                variant="tertiary"
                size="xsmall"
                label={t('chat:trustedSources.viewTrustedSources')}
                onClick={handleModalOpen}
              />
            )}
          </div>
        );
      })}
    </DropdownMenu>
  );

  return (
    <>
      {dropdownMenu}
      {modalOpen && (
        <TrustedSourcesModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
